/**
 * FleetStore — contenedor normalizado de la flota, fuera de React.
 *
 * Por qué existe
 * ──────────────
 * Hasta la fase 2 la flota vivía en `useState` dentro de `AppShell`: un array
 * `devices` y un objeto `traces`. Cada mensaje WS creaba un array nuevo, así
 * que el nodo RAÍZ del árbol se re-renderizaba ~1 vez por posición recibida y
 * arrastraba consigo la página entera. Con 30 vehículos eso se aguanta; con
 * 1.000 el trabajo por tick crece linealmente en TODOS los consumidores a la
 * vez (mapa, lista, contadores, panel de detalle) aunque solo se haya movido
 * un vehículo.
 *
 * Este store invierte la relación: los datos viven en Maps mutables fuera de
 * React y cada consumidor se suscribe SOLO a la porción que pinta —
 * normalmente un IMEI. Un mensaje de un vehículo despierta a su marcador y a
 * su fila de la lista, y a nadie más. El coste por tick pasa de O(flota) a
 * O(vehículos que cambiaron).
 *
 * Contrato con `useSyncExternalStore`
 * ───────────────────────────────────
 * Todos los `get*` son snapshots ESTABLES: devuelven exactamente la misma
 * referencia mientras no haya cambiado el dato. React llama a `getSnapshot`
 * varias veces por render y compara por identidad; devolver un objeto nuevo
 * cada llamada provoca un bucle infinito de renders. Por eso:
 *   - los objetos de device se reemplazan (nunca se mutan in-place),
 *   - los derivados caros (array de flota, roster, contadores) se cachean y
 *     se reconstruyen de forma perezosa solo cuando quedaron sucios.
 *
 * Notificación por lotes
 * ──────────────────────
 * `batch(fn)` acumula los cambios y emite UNA sola tanda de notificaciones al
 * final. El coalescer de `WebSocketContext` ya entrega los mensajes en
 * ráfagas; procesarlas dentro de un batch evita despertar al mismo suscriptor
 * N veces por flush. Fuera de un batch cada mutación notifica en el acto.
 */

import { deviceChipKey } from "../../utils/deviceState.js";

/** Snapshots vacíos compartidos — identidad estable para los consumidores. */
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

/** Claves de los contadores por estado (mismas cubetas que los chips). */
const COUNT_KEYS = ["moving", "stopped", "disconnected", "out_of_service"];

/**
 * Campos de IDENTIDAD del vehículo: los que deciden en qué grupo cae en la
 * lista y si coincide con una búsqueda.
 *
 * Se separan de la telemetría porque cambian por vías distintas y a ritmos
 * incomparables: estos solo se mueven con una edición administrativa (un
 * refetch, un `refreshDevices`), mientras que velocidad o estado llegan cada
 * pocos segundos por WebSocket. Quien depende de la ESTRUCTURA de la lista
 * puede escuchar solo estos y recalcular casi nunca.
 */
const METADATA_KEYS = [
  "group_ids",
  "group_names",
  "plate",
  "name",
  "vehicle_type",
  "driver_name",
  "driver_id",
  // Estado administrativo del vehículo (`vehicles.status`): cambia solo desde
  // /admin, como el resto de este grupo, pero decide en qué cubeta cae la fila
  // y con qué etiqueta se pinta — no es telemetría.
  "vehicle_status",
];

/** Bucket de contador al que pertenece un device, o `null` si no existe. */
function chipKeyOf(device) {
  if (!device) return null;
  return deviceChipKey(device);
}

/** ¿Cambia alguno de los campos de `patch` respecto de `prev`? */
function patchChangesAnything(prev, patch) {
  for (const k in patch) {
    if (prev[k] !== patch[k]) return true;
  }
  return false;
}

/**
 * Crea un store de flota.
 *
 * @returns {object} API del store (ver los métodos documentados abajo).
 */
export function createFleetStore() {
  // ── Datos ────────────────────────────────────────────────────────────────
  /** @type {Map<string, object>} imei → device (objeto inmutable, se reemplaza) */
  const devices = new Map();
  /** @type {Map<string, Array>} imei → trace newest-first (array inmutable) */
  const traces = new Map();

  // ── Suscriptores ─────────────────────────────────────────────────────────
  /** @type {Map<string, Set<Function>>} */
  const deviceListeners = new Map();
  /** @type {Map<string, Set<Function>>} */
  const traceListeners = new Map();
  const rosterListeners = new Set();
  const arrayListeners = new Set();
  const countsListeners = new Set();
  const statusListeners = new Set();
  const traceInterestListeners = new Set();
  const tracesObjectListeners = new Set();
  const metadataListeners = new Set();

  // ── Estado sucio (pendiente de notificar) ────────────────────────────────
  let batchDepth = 0;
  const dirtyDevices = new Set();
  const dirtyTraces = new Set();
  let rosterDirty = false;
  let arrayDirty = false;
  let countsDirty = false;
  let statusDirty = false;
  let traceInterestDirty = false;
  let metadataDirty = false;
  let metadataVersion = 0;

  // ── Cachés de snapshots derivados ────────────────────────────────────────
  let rosterCache = EMPTY_ARRAY;
  let arrayCache = EMPTY_ARRAY;
  let arrayStale = false;
  let tracesObjectCache = EMPTY_OBJECT;
  let tracesObjectStale = false;
  let counts = Object.freeze({ moving: 0, stopped: 0, disconnected: 0, out_of_service: 0 });
  // `isSocketOpen` guarda la función que lee el readyState real del socket.
  // Viaja en el status (y no como valor de retorno del motor) para que el
  // provider no tenga que re-renderizar al conectar o desconectar.
  let status = Object.freeze({
    loading: true,
    wsConnected: false,
    isSocketOpen: null,
  });
  let traceInterest = null; // null = interés en TODA la flota

  // ── Notificación ─────────────────────────────────────────────────────────

  function emitAll(set) {
    if (!set || set.size === 0) return;
    // Copia defensiva: un listener puede desuscribirse durante la emisión
    // (React lo hace al desmontar en el mismo commit).
    for (const fn of [...set]) {
      try {
        fn();
      } catch (err) {
        console.error("[fleetStore] listener failed:", err);
      }
    }
  }

  function flush() {
    if (batchDepth > 0) return;

    if (dirtyDevices.size > 0) {
      const ids = [...dirtyDevices];
      dirtyDevices.clear();
      for (const imei of ids) emitAll(deviceListeners.get(imei));
    }
    if (dirtyTraces.size > 0) {
      const ids = [...dirtyTraces];
      dirtyTraces.clear();
      tracesObjectStale = true;
      for (const imei of ids) emitAll(traceListeners.get(imei));
      emitAll(tracesObjectListeners);
    }
    if (rosterDirty) {
      rosterDirty = false;
      emitAll(rosterListeners);
    }
    if (arrayDirty) {
      arrayDirty = false;
      emitAll(arrayListeners);
    }
    if (countsDirty) {
      countsDirty = false;
      emitAll(countsListeners);
    }
    if (statusDirty) {
      statusDirty = false;
      emitAll(statusListeners);
    }
    if (traceInterestDirty) {
      traceInterestDirty = false;
      emitAll(traceInterestListeners);
    }
    if (metadataDirty) {
      metadataDirty = false;
      emitAll(metadataListeners);
    }
    // Declarada más abajo — el hoisting de `function` la deja disponible aquí.
    // Va al final para que un `batch()` emita las porciones auxiliares en la
    // misma tanda que los devices.
    flushAux();
  }

  /** Marca un device como cambiado e invalida los derivados que lo incluyen. */
  function markDeviceDirty(imei) {
    dirtyDevices.add(imei);
    arrayStale = true;
    arrayDirty = true;
  }

  /** Marca que cambió algún campo de identidad (ver `METADATA_KEYS`). */
  function markMetadataDirty() {
    metadataVersion += 1;
    metadataDirty = true;
  }

  /** Ajuste incremental de contadores ante un cambio de un device. */
  function bumpCounts(prevDevice, nextDevice) {
    const prevKey = chipKeyOf(prevDevice);
    const nextKey = chipKeyOf(nextDevice);
    if (prevKey === nextKey) return;
    const next = { ...counts };
    if (prevKey) next[prevKey] = Math.max(0, next[prevKey] - 1);
    if (nextKey) next[nextKey] += 1;
    counts = Object.freeze(next);
    countsDirty = true;
  }

  /** Recuento completo — solo para reemplazos masivos del roster. */
  function recomputeCounts() {
    const next = { moving: 0, stopped: 0, disconnected: 0, out_of_service: 0 };
    for (const device of devices.values()) {
      const key = chipKeyOf(device);
      if (key) next[key] += 1;
    }
    for (const k of COUNT_KEYS) {
      if (next[k] !== counts[k]) {
        counts = Object.freeze(next);
        countsDirty = true;
        return;
      }
    }
  }

  // ── Lectura (snapshots estables) ─────────────────────────────────────────

  /** Device por IMEI, o `undefined` si no está en la flota. */
  function getDevice(imei) {
    return devices.get(imei);
  }

  /** Trace (newest-first) por IMEI. Devuelve un array vacío compartido si no hay. */
  function getTrace(imei) {
    return traces.get(imei) ?? EMPTY_ARRAY;
  }

  /** Lista de IMEIs de la flota. Identidad estable mientras no cambie la membresía. */
  function getRoster() {
    return rosterCache;
  }

  /**
   * Array completo de devices — puente para las pantallas que todavía razonan
   * sobre la flota entera (Dashboard, Comandos, Geocercas, Alertas).
   *
   * Se reconstruye de forma perezosa: quien no llame a esto no paga el O(N).
   * Suscribirse a este snapshot es volver al coste por tick de la fase 2, así
   * que en el Monitor NADIE debería hacerlo — ahí se usa roster + por-IMEI.
   */
  function getDevicesArray() {
    if (arrayStale) {
      arrayStale = false;
      arrayCache = rosterCache.map((imei) => devices.get(imei)).filter(Boolean);
    }
    return arrayCache;
  }

  /**
   * Objeto `{ [imei]: trace }` con TODAS las trazas — el mismo puente que
   * `getDevicesArray`, con la misma advertencia: cambia con cualquier traza.
   */
  function getTracesObject() {
    if (tracesObjectStale) {
      tracesObjectStale = false;
      const next = {};
      for (const [imei, points] of traces) next[imei] = points;
      tracesObjectCache = next;
    }
    return tracesObjectCache;
  }

  /** Contadores por estado de TODA la flota, mantenidos incrementalmente. */
  function getCounts() {
    return counts;
  }

  /** `{ loading, wsConnected, isSocketOpen }` — estado agregado de la flota. */
  function getStatus() {
    return status;
  }

  /**
   * Contador que avanza cuando cambia algún campo de IDENTIDAD de algún
   * vehículo (ver `METADATA_KEYS`). Lo consume quien deriva la ESTRUCTURA de
   * la lista —agrupación, coincidencia de búsqueda— y necesita recalcular
   * cuando un vehículo cambia de grupo, pero no con cada reporte de posición.
   */
  function getMetadataVersion() {
    return metadataVersion;
  }

  /** Conjunto de IMEIs cuyas trazas interesan, o `null` = toda la flota. */
  function getTraceInterest() {
    return traceInterest;
  }

  // ── Suscripción ──────────────────────────────────────────────────────────

  function subscribeKeyed(map, key, listener) {
    let set = map.get(key);
    if (!set) {
      set = new Set();
      map.set(key, set);
    }
    set.add(listener);
    return () => {
      const current = map.get(key);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) map.delete(key);
    };
  }

  function subscribeSet(set, listener) {
    set.add(listener);
    return () => set.delete(listener);
  }

  // ── Mutación ─────────────────────────────────────────────────────────────

  /**
   * Agrupa mutaciones y emite una sola tanda de notificaciones al salir.
   * Reentrante: los batches anidados solo cuentan para la profundidad.
   */
  function batch(fn) {
    batchDepth += 1;
    try {
      return fn();
    } finally {
      batchDepth -= 1;
      flush();
    }
  }

  /**
   * Aplica un parche a un device existente.
   * No-op (devuelve `false`) si el IMEI no está en la flota o si ningún campo
   * cambia de valor — ese guard es el que evita despertar suscriptores por
   * heartbeats que repiten la telemetría anterior.
   */
  function patchDevice(imei, patch) {
    const prev = devices.get(imei);
    if (!prev || !patch) return false;
    if (!patchChangesAnything(prev, patch)) return false;
    const next = { ...prev, ...patch };
    devices.set(imei, next);
    bumpCounts(prev, next);
    markDeviceDirty(imei);
    // Los parches de telemetría (el 99 % del tráfico) no tocan ninguno de estos
    // campos, así que este chequeo casi nunca dispara nada.
    for (const k of METADATA_KEYS) {
      if (k in patch && prev[k] !== next[k]) {
        markMetadataDirty();
        break;
      }
    }
    flush();
    return true;
  }

  /**
   * Inserta o reemplaza un device completo. Usado por el camino de
   * `device_status` para el IMEI provisional previo al fetch inicial.
   */
  function upsertDevice(device) {
    if (!device?.imei) return false;
    const { imei } = device;
    const prev = devices.get(imei);
    if (prev === device) return false;
    devices.set(imei, device);
    bumpCounts(prev, device);
    markDeviceDirty(imei);
    markMetadataDirty();
    if (!prev) {
      rosterCache = [...rosterCache, imei];
      rosterDirty = true;
    }
    flush();
    return true;
  }

  /**
   * Saca un device de la flota sin esperar al próximo snapshot. Es el camino
   * de un vehículo que pasa a robado: deja de existir para el Monitor y los
   * selectores en el acto (ver `isHiddenVehicleStatus`). También el de un
   * equipo retirado o borrado del inventario (aviso WS `device_removed`).
   *
   * @returns {boolean} `false` si el IMEI no estaba.
   */
  function removeDevice(imei) {
    const prev = devices.get(imei);
    if (!prev) return false;
    devices.delete(imei);
    if (traces.delete(imei)) dirtyTraces.add(imei);
    markDeviceDirty(imei);
    markMetadataDirty();
    bumpCounts(prev, null);
    rosterCache = rosterCache.filter((id) => id !== imei);
    rosterDirty = true;
    flush();
    return true;
  }

  /**
   * Reemplaza el roster completo con el snapshot autoritativo del backend.
   *
   * `mergeFn(fresh, existing)` decide el objeto final de cada IMEI ya
   * conocido: es donde vive el version-guard REST/WS (qué campos de memoria
   * sobreviven al snapshot HTTP). Sin `mergeFn` gana el objeto fresco.
   *
   * Los IMEIs ausentes del snapshot se eliminan junto con su traza — es el
   * único punto donde la flota se poda, así que también sirve de recolector
   * para los Maps auxiliares del motor.
   *
   * @returns {string[]} IMEIs eliminados en esta pasada.
   */
  function setRoster(list, mergeFn) {
    const incoming = Array.isArray(list) ? list : [];
    const nextIds = [];
    const nextIdSet = new Set();

    batchDepth += 1;
    try {
      for (const fresh of incoming) {
        if (!fresh?.imei) continue;
        const { imei } = fresh;
        if (nextIdSet.has(imei)) continue;
        nextIdSet.add(imei);
        nextIds.push(imei);
        const existing = devices.get(imei);
        const merged = mergeFn ? mergeFn(fresh, existing) : fresh;
        if (existing !== merged) {
          devices.set(imei, merged);
          markDeviceDirty(imei);
          // Un refetch es la vía por la que llegan las ediciones administrativas
          // (cambios de grupo, de placa, de conductor). Marcar sin comparar
          // campo por campo: pasa unas pocas veces por sesión.
          markMetadataDirty();
        }
      }

      const removed = [];
      for (const imei of devices.keys()) {
        if (!nextIdSet.has(imei)) removed.push(imei);
      }
      for (const imei of removed) {
        devices.delete(imei);
        if (traces.delete(imei)) dirtyTraces.add(imei);
        markDeviceDirty(imei);
      }

      // La identidad del roster solo cambia si cambió la membresía: un refetch
      // que devuelve la misma flota no debe invalidar los memos de nadie.
      const sameMembership =
        rosterCache.length === nextIds.length &&
        rosterCache.every((imei) => nextIdSet.has(imei));
      if (!sameMembership) {
        rosterCache = nextIds;
        rosterDirty = true;
        arrayStale = true;
        arrayDirty = true;
      }

      recomputeCounts();
      return removed;
    } finally {
      batchDepth -= 1;
      flush();
    }
  }

  /** Reemplaza la traza de un IMEI. `null`/`undefined` la borra. */
  function setTrace(imei, points) {
    const prev = traces.get(imei);
    if (points == null) {
      if (!traces.delete(imei)) return false;
      dirtyTraces.add(imei);
      flush();
      return true;
    }
    if (prev === points) return false;
    traces.set(imei, points);
    dirtyTraces.add(imei);
    flush();
    return true;
  }

  /**
   * Actualiza la traza con un updater que recibe el array actual (o `[]`).
   * Devolver el mismo array = no-op, igual que un updater de `useState`.
   */
  function updateTrace(imei, updater) {
    const prev = traces.get(imei) ?? EMPTY_ARRAY;
    const next = updater(prev);
    if (next === prev) return false;
    return setTrace(imei, next);
  }

  /** Parche del status agregado (`loading`, `wsConnected`, `isSocketOpen`). */
  function setStatus(patch) {
    if (!patch) return false;
    if (!patchChangesAnything(status, patch)) return false;
    status = Object.freeze({ ...status, ...patch });
    statusDirty = true;
    flush();
    return true;
  }

  /**
   * Declara qué trazas interesan (las del viewport del mapa, típicamente).
   * `null` restaura el interés en toda la flota.
   *
   * Solo notifica cuando el CONJUNTO cambia de verdad: el mapa recalcula su
   * viewport en cada `moveend` y la mayoría de las veces devuelve el mismo
   * set.
   */
  function setTraceInterest(imeis) {
    if (imeis == null) {
      if (traceInterest === null) return false;
      traceInterest = null;
      traceInterestDirty = true;
      markMetadataDirty();
      flush();
      return true;
    }
    const next = imeis instanceof Set ? imeis : new Set(imeis);
    const prev = traceInterest;
    if (prev && prev.size === next.size) {
      let same = true;
      for (const imei of next) {
        if (!prev.has(imei)) {
          same = false;
          break;
        }
      }
      if (same) return false;
    }
    traceInterest = next;
    traceInterestDirty = true;
    flush();
    return true;
  }

  // ── Porciones auxiliares del motor ───────────────────────────────────────
  //
  // Viven aquí y no en `useState` porque el motor corre dentro de
  // `<FleetProvider>`: cualquier estado de React suyo re-renderizaría a TODOS
  // sus hijos, o sea la app entera, que es justo lo que la fase 3 elimina.
  // Todas siguen el mismo patrón que los devices: Map por IMEI + suscripción
  // por IMEI + una vista de objeto para los consumidores que aún necesitan el
  // conjunto completo.

  /** @type {Map<string, object>} imei → { from, to, fromTs, toTs, reason } */
  const syncRequests = new Map();
  const syncRequestListeners = new Map();
  const syncRequestsObjectListeners = new Set();
  const dirtySyncRequests = new Set();
  let syncRequestsObjectCache = EMPTY_OBJECT;
  let syncRequestsObjectStale = false;
  let syncRequestsObjectDirty = false;

  /** @type {Map<string, object>} imei → último command_update recibido */
  const commands = new Map();
  const commandListeners = new Map();
  const commandsObjectListeners = new Set();
  const dirtyCommands = new Set();
  let commandsObjectCache = EMPTY_OBJECT;
  let commandsObjectStale = false;
  let commandsObjectDirty = false;

  /** Alertas ENTER/EXIT efímeras (toasts). */
  let geofenceAlerts = EMPTY_ARRAY;
  const geofenceAlertListeners = new Set();
  let geofenceAlertsDirty = false;

  function flushAux() {
    if (batchDepth > 0) return;
    if (dirtySyncRequests.size > 0) {
      const ids = [...dirtySyncRequests];
      dirtySyncRequests.clear();
      for (const imei of ids) emitAll(syncRequestListeners.get(imei));
    }
    if (syncRequestsObjectDirty) {
      syncRequestsObjectDirty = false;
      emitAll(syncRequestsObjectListeners);
    }
    if (dirtyCommands.size > 0) {
      const ids = [...dirtyCommands];
      dirtyCommands.clear();
      for (const imei of ids) emitAll(commandListeners.get(imei));
    }
    if (commandsObjectDirty) {
      commandsObjectDirty = false;
      emitAll(commandsObjectListeners);
    }
    if (geofenceAlertsDirty) {
      geofenceAlertsDirty = false;
      emitAll(geofenceAlertListeners);
    }
  }

  function getSyncRequest(imei) {
    return syncRequests.get(imei) ?? null;
  }

  function getSyncRequestsObject() {
    if (syncRequestsObjectStale) {
      syncRequestsObjectStale = false;
      const next = {};
      for (const [imei, request] of syncRequests) next[imei] = request;
      syncRequestsObjectCache = next;
    }
    return syncRequestsObjectCache;
  }

  /**
   * Registra una petición de sincronización para un IMEI.
   *
   * `merge`: si ya había una sin resolver se CONSERVA el `from`/`fromTs`
   * original (lo que el usuario llegó a ver) y solo avanza el destino. Es el
   * edge case #8 del resync — el marcador tiene que deslizarse desde donde
   * quedó, no desde el penúltimo punto.
   */
  function setSyncRequest(imei, request) {
    const prev = syncRequests.get(imei);
    if (prev) {
      syncRequests.set(imei, {
        ...prev,
        to: request.to,
        toTs: request.toTs,
      });
    } else {
      syncRequests.set(imei, request);
    }
    dirtySyncRequests.add(imei);
    syncRequestsObjectStale = true;
    syncRequestsObjectDirty = true;
    flushAux();
  }

  function clearSyncRequest(imei) {
    if (!syncRequests.delete(imei)) return false;
    dirtySyncRequests.add(imei);
    syncRequestsObjectStale = true;
    syncRequestsObjectDirty = true;
    flushAux();
    return true;
  }

  function getCommand(imei) {
    return commands.get(imei) ?? null;
  }

  function getCommandsObject() {
    if (commandsObjectStale) {
      commandsObjectStale = false;
      const next = {};
      for (const [imei, cmd] of commands) next[imei] = cmd;
      commandsObjectCache = next;
    }
    return commandsObjectCache;
  }

  function setCommand(imei, command) {
    commands.set(imei, command);
    dirtyCommands.add(imei);
    commandsObjectStale = true;
    commandsObjectDirty = true;
    flushAux();
  }

  function getGeofenceAlerts() {
    return geofenceAlerts;
  }

  /** Añade una alerta respetando el tope (las más viejas se descartan). */
  function pushGeofenceAlert(alert, max) {
    const next = [...geofenceAlerts, alert];
    geofenceAlerts = next.length > max ? next.slice(-max) : next;
    geofenceAlertsDirty = true;
    flushAux();
  }

  function removeGeofenceAlert(id) {
    const next = geofenceAlerts.filter((a) => a.id !== id);
    if (next.length === geofenceAlerts.length) return false;
    geofenceAlerts = next;
    geofenceAlertsDirty = true;
    flushAux();
    return true;
  }

  /** Vacía el store por completo (logout / desmontaje del provider). */
  function reset() {
    batchDepth += 1;
    try {
      for (const imei of devices.keys()) markDeviceDirty(imei);
      for (const imei of traces.keys()) dirtyTraces.add(imei);
      devices.clear();
      traces.clear();
      rosterCache = EMPTY_ARRAY;
      arrayCache = EMPTY_ARRAY;
      arrayStale = false;
      tracesObjectCache = EMPTY_OBJECT;
      tracesObjectStale = false;
      rosterDirty = true;
      arrayDirty = true;
      counts = Object.freeze({ moving: 0, stopped: 0, disconnected: 0, out_of_service: 0 });
      countsDirty = true;
      traceInterest = null;
      traceInterestDirty = true;

      for (const imei of syncRequests.keys()) dirtySyncRequests.add(imei);
      syncRequests.clear();
      syncRequestsObjectCache = EMPTY_OBJECT;
      syncRequestsObjectStale = false;
      syncRequestsObjectDirty = true;
      for (const imei of commands.keys()) dirtyCommands.add(imei);
      commands.clear();
      commandsObjectCache = EMPTY_OBJECT;
      commandsObjectStale = false;
      commandsObjectDirty = true;
      geofenceAlerts = EMPTY_ARRAY;
      geofenceAlertsDirty = true;
    } finally {
      batchDepth -= 1;
      flush();
    }
  }

  return {
    // lectura
    getDevice,
    getTrace,
    getRoster,
    getDevicesArray,
    getTracesObject,
    getCounts,
    getStatus,
    getTraceInterest,
    getMetadataVersion,
    getSyncRequest,
    getSyncRequestsObject,
    getCommand,
    getCommandsObject,
    getGeofenceAlerts,
    get size() {
      return devices.size;
    },
    // suscripción
    subscribeDevice: (imei, listener) =>
      subscribeKeyed(deviceListeners, imei, listener),
    subscribeTrace: (imei, listener) =>
      subscribeKeyed(traceListeners, imei, listener),
    subscribeRoster: (listener) => subscribeSet(rosterListeners, listener),
    subscribeDevicesArray: (listener) => subscribeSet(arrayListeners, listener),
    subscribeTracesObject: (listener) =>
      subscribeSet(tracesObjectListeners, listener),
    subscribeCounts: (listener) => subscribeSet(countsListeners, listener),
    subscribeStatus: (listener) => subscribeSet(statusListeners, listener),
    subscribeTraceInterest: (listener) =>
      subscribeSet(traceInterestListeners, listener),
    subscribeMetadata: (listener) => subscribeSet(metadataListeners, listener),
    subscribeSyncRequest: (imei, listener) =>
      subscribeKeyed(syncRequestListeners, imei, listener),
    subscribeSyncRequestsObject: (listener) =>
      subscribeSet(syncRequestsObjectListeners, listener),
    subscribeCommand: (imei, listener) =>
      subscribeKeyed(commandListeners, imei, listener),
    subscribeCommandsObject: (listener) =>
      subscribeSet(commandsObjectListeners, listener),
    subscribeGeofenceAlerts: (listener) =>
      subscribeSet(geofenceAlertListeners, listener),
    // mutación
    batch,
    patchDevice,
    upsertDevice,
    removeDevice,
    setRoster,
    setTrace,
    updateTrace,
    setStatus,
    setTraceInterest,
    setSyncRequest,
    clearSyncRequest,
    setCommand,
    pushGeofenceAlert,
    removeGeofenceAlert,
    reset,
  };
}

export default createFleetStore;
